<template>
  <div>
    <BaseReportTable
      :resultados="resultados"
      :total-items="totalItems"
      :items-por-pagina="itemsPorPagina"
      :pagina-actual="paginaActual"
      :loading="loading"
      :headers="saldosHTCHeaders"
      :es-operacion="esOperacion"
      @cambiar-pagina="$emit('cambiar-pagina', $event)"
      @cambiar-items-por-pagina="$emit('cambiar-items-por-pagina', $event)"
    >
      <template v-slot:item.legajo="{ item }">
        <a
          v-if="item.legajo"
          :href="getGestionarUrl(item.legajo)"
          target="_blank"
          class="legajo-link"
        >
          {{ item.legajo }}
        </a>
        <template v-else>-</template>
      </template>

      <template v-slot:item.operatoria_linea="{ item }">
        {{ item.operatoria_linea || '-' }}
      </template>

      <template v-slot:item.nombre_razon_social="{ item }">
        {{ item.nombre_razon_social ? item.nombre_razon_social.toUpperCase() : '-' }}
      </template>

      <template v-slot:item.cuit="{ item }">
        {{ item.cuit || '-' }}
      </template>

      <template v-slot:item.departamento="{ item }">
        {{ getDepartamentoNombre(item) }}
      </template>

      <template v-slot:item.persona="{ item }">
        <span class="persona-text">{{ item.persona || '-' }}</span>
      </template>

      <template v-slot:item.fecha_ingreso="{ item }">
        {{ formatearFecha(item.fecha_ingreso) }}
      </template>

      <template v-slot:item.estado="{ item }">
        <span class="estado-text">{{ item.estado || '-' }}</span>
      </template>

      <!-- Templates para campos específicos de Saldos HTC -->
      <template v-slot:item.fecha_otorgamiento="{ item }">
        {{ item.fecha_otorgamiento || '-' }}
      </template>

      <template v-slot:item.fecha_vencimiento_obligacion="{ item }">
        {{ item.fecha_vencimiento_obligacion || '-' }}
      </template>

      <template v-slot:item.fecha_ultimo_vencimiento="{ item }">
        {{ item.fecha_ultimo_vencimiento || '-' }}
      </template>

      <template v-slot:item.saldo_inicial_periodo="{ item }">
        {{ formatoNumero(item.saldo_inicial_periodo) }}
      </template>

      <template v-slot:item.intereses_compensatorios="{ item }">
        {{ formatoNumero(item.intereses_compensatorios) }}
      </template>

      <template v-slot:item.debitos_del_periodo="{ item }">
        {{ formatoNumero(item.debitos_del_periodo) }}
      </template>

      <template v-slot:item.creditos_del_periodo="{ item }">
        {{ formatoNumero(item.creditos_del_periodo) }}
      </template>

      <template v-slot:item.saldo_final_periodo="{ item }">
        {{ formatoNumero(item.saldo_final_periodo) }}
      </template>

      <template v-slot:item.estado_morosidad="{ item }">
        {{ item.estado_morosidad || '-' }}
      </template>

      <template v-slot:item.responsable_gestion="{ item }">
        {{ item.responsable_gestion || '-' }}
      </template>

      <template v-slot:item.estado_manual="{ item }">
        {{ item.estado_manual || '-' }}
      </template>

      <!-- Slot para fila de totales -->
      <template v-slot:totals-row="{ totals }">
        <!-- BaseReportTable ya incluye 8 columnas base (Legajo hasta Estado) -->
        <!-- El slot comienza en la columna 9, pero necesitamos desplazar una columna para alinear correctamente -->

        <!-- Celda vacía adicional para ajustar alineación -->
        <td></td>

        <!-- Columna 9: Fecha de Otorgamiento (vacía) -->
        <td></td>

        <!-- Columna 10: Fecha de Vencimiento de la Obligación (vacía) -->
        <td></td>

        <!-- Columna 12: Saldo Inicial del Período (total) -->
        <td class="text-right font-weight-bold">
          {{ formatoNumero(totalSaldoInicial) }}
        </td>

        <!-- Columna 13: Intereses Compensatorios (total) -->
        <td class="text-right font-weight-bold">
          {{ formatoNumero(totalInteresesCompensatorios) }}
        </td>

        <!-- Columna 14: Débitos del Período (total) -->
        <td class="text-right font-weight-bold">
          {{ formatoNumero(totalDebitos) }}
        </td>

        <!-- Columna 15: Créditos del Período (total) -->
        <td class="text-right font-weight-bold">
          {{ formatoNumero(totalCreditos) }}
        </td>

        <!-- Columna 16: Saldo Final del Período (total) -->
        <td class="text-right font-weight-bold">
          {{ formatoNumero(totalSaldoFinal) }}
        </td>

        <!-- Columna 17: Estado de Gestión de Cobranzas (vacía) -->
        <td></td>

        <!-- Columna 18: Responsable de la Gestión (vacía) -->
        <td></td>

        <!-- Columna 19: Estado Manual (vacía) -->
        <td></td>
      </template>
    </BaseReportTable>
  </div>
</template>

<script>
import BaseReportTable from './BaseReportTable.vue';
import ReportCalculations from '../ReportCalculations';

export default {
  name: 'SaldosHTCReportTable',
  components: {
    BaseReportTable,
  },
  mixins: [ReportCalculations],
  props: {
    resultados: {
      type: Array,
      default: () => [],
    },
    totalItems: {
      type: Number,
      default: 0,
    },
    itemsPorPagina: {
      type: Number,
      default: 10,
    },
    paginaActual: {
      type: Number,
      default: 1,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    esOperacion: {
      type: String,
      default: '',
    },
    totales: {
      type: Object,
      default: () => ({}),
    },
  },
  computed: {
    saldosHTCHeaders() {
      // Importar el helper para obtener los headers correctos
      const ReportesHelpers = require('@/utils/reportesHelpers').ReportesHelpers;

      // Obtener headers para el reporte HTC con tipoReporte específico
      const headers = ReportesHelpers.obtenerHeadersTabla(
        [], // tiposServicios vacío, no se usa para reportes especiales
        13, // filtroTipoServicio - Financiamiento
        'Reporte de Saldos - HTC' // tipoReporte
      );

      return headers;
    },
    totalSaldoInicial() {
      return this.resultados.reduce((total, item) => {
        if (item._esSubtotal) return total;
        return total + parseFloat(item.saldo_inicial_periodo || 0);
      }, 0);
    },
    totalInteresesCompensatorios() {
      return this.resultados.reduce((total, item) => {
        if (item._esSubtotal) return total;
        return total + parseFloat(item.intereses_compensatorios || 0);
      }, 0);
    },
    totalDebitos() {
      return this.resultados.reduce((total, item) => {
        if (item._esSubtotal) return total;
        return total + parseFloat(item.debitos_del_periodo || 0);
      }, 0);
    },
    totalCreditos() {
      return this.resultados.reduce((total, item) => {
        if (item._esSubtotal) return total;
        return total + parseFloat(item.creditos_del_periodo || 0);
      }, 0);
    },
    totalSaldoFinal() {
      return this.resultados.reduce((total, item) => {
        if (item._esSubtotal) return total;
        return total + parseFloat(item.saldo_final_periodo || 0);
      }, 0);
    },
  },
  methods: {
    getGestionarUrl(legajo) {
      const baseUrl = process.env.VUE_APP_GESTIONAR_URL || 'http://localhost:8080';
      return `${baseUrl}/gestionar/${legajo}`;
    },
    formatearFecha(fecha) {
      if (!fecha) return '-';
      try {
        return new Date(fecha).toLocaleDateString('es-ES');
      } catch {
        return fecha;
      }
    },
    getDepartamentoNombre(item) {
      // Si el item ya tiene el campo departamento mapeado, usarlo
      if (item.departamento) {
        return item.departamento;
      }
      // Intentar obtener departamento desde diferentes estructuras
      if (item.localidad && item.localidad.departamento && item.localidad.departamento.nombre) {
        return item.localidad.departamento.nombre;
      }
      if (item.cliente && item.cliente.localidad && item.cliente.localidad.departamento && item.cliente.localidad.departamento.nombre) {
        return item.cliente.localidad.departamento.nombre;
      }
      return '-';
    },
  },
  emits: ['cambiar-pagina', 'cambiar-items-por-pagina'],
};
</script>

<style scoped>
.persona-text {
  color: #000000;
  font-weight: normal;
}

.estado-text {
  color: #000000;
  font-weight: normal;
}

.legajo-link {
  color: #1976d2;
  text-decoration: none;
  font-weight: 500;
}

.legajo-link:hover {
  color: #1565c0;
  text-decoration: underline;
}
</style>
